// Hook to find if a tenant is on a trial

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';
import { useParams } from 'react-router';

/**
 * Custom hook that finds whether a partner is on a trial or not.
 * It uses the partnerTenantId if any, otherwise, the user's session information.
 *
 * @returns {boolean}
 *
 * @example
 * const displayTrialBanner = useIsOnTrialFromPartnerTenantInfo();
 */
export const useIsOnTrialFromPartnerTenantInfo = (): boolean => {
    const { partnerId } = useParams();
    const session = useSession();
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;

    if (partnerTenant && partnerTenant.id !== session.tenant?.id) {
        const settings = partnerTenant?.settings || {};
        const hubType = settings?.hubType || 'None';
        const licensedHub = hubType !== 'None';

        const isTrialCustomer = !licensedHub && !!settings?.trialEndDate;

        const inTrial =
            isTrialCustomer &&
            settings?.trialEndDate &&
            new Date(settings?.trialEndDate).getTime() > new Date().getTime();

        // If the user/tenant does not have a licensedHub AND they have a trialEndDate set, they are a trial customer.
        // Based on this, we can further determine if they are in an active trial or an expired trial.
        return !!inTrial;
    }

    return !!session?.sessionSettings?.inTrial;
};
